/**
 * ZoneSystem — manages persistent ground zones in the game world.
 *
 * Creates, tracks, and queries zones with support for:
 * - Three shape geometries: circle, cone, line
 * - Mobile zones (velocity drift, entity following)
 * - Per-entity tracking (entry times, one-shot triggers)
 * - Callbacks (onEntityExit, onExpiry)
 * - Movement suppression (dash, charge)
 * - Projectile blocking
 * - DoT and entry damage
 * - Zone expansion over time
 *
 * One system instance per scene. Skills create zones via create(),
 * query via getZonesAt/isInsideType, and the system ticks all zones
 * each frame via update().
 *
 * @see ADR-064 (shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface ZoneConfig {
  id: string;
  type: string;
  position: { x: number; y: number };
  durationMs: number;                   // -1 = permanent
  sourceSkillId: string;

  // Shape — exactly one geometry
  shape: 'circle' | 'cone' | 'line';
  radiusTiles?: number;                 // circle
  lengthTiles?: number;                 // cone or line
  widthTiles?: number;                  // line
  angleRange?: number;                  // cone: full arc in degrees
  facingAngle?: number;                 // cone or line: radians

  // DoT
  dotDefId?: string;
  dotIntensity?: number;
  dotDurationMs?: number;
  tickIntervalMs?: number;             // default 500
  dotStackingRule?: string;            // e.g. 'independent' (Saturation)
  dotMaxStacks?: number;               // max concurrent independent instances

  // Entry damage
  entryDamage?: number;

  // Movement
  speedMultiplier?: number;
  suppressDash?: boolean;
  suppressCharge?: boolean;
  impassable?: boolean;

  // Projectile blocking
  blocksProjectiles?: boolean;

  // Detection
  detectionReduction?: number;
  suppressesSound?: boolean;

  // Mobility
  velocity?: { dx: number; dy: number };
  followEntity?: string;

  // Callbacks
  onEntityEnter?: (entityId: string, zone: ActiveZone) => void;
  onEntityExit?: (entityId: string, zone: ActiveZone, dwellMs: number, exitX: number, exitY: number) => void;
  onExpiry?: (zone: ActiveZone) => void;

  // One-shot
  oneShot?: boolean;

  // Expansion
  expandsOverTime?: boolean;
  expansionRateMs?: number;
  /** Tiles added per expansion period (expansionRateMs). Default 1.0 if not set. */
  expansionTiles?: number;
  maxRadiusTiles?: number;

  // Visual
  tintColor?: number;
  alpha?: number;

  // Entity immunity
  /**
   * Entity ID immune to this zone's effects (DoT, entry damage, movement
   * suppression, etc.). Used by Cultivate to exclude the player from their
   * own fire zones.
   */
  friendlyEntityId?: string;
}

// ---------------------------------------------------------------------------
// Active zone instance
// ---------------------------------------------------------------------------

export interface ActiveZone {
  readonly config: ZoneConfig;
  remainingMs: number;
  currentRadiusTiles: number;
  readonly entitiesInside: Set<string>;
  readonly entityEntryTimes: Map<string, number>;
  readonly usedByEntities: Set<string>;
}

// ---------------------------------------------------------------------------
// Zone events emitted during update
// ---------------------------------------------------------------------------

export interface ZoneEvent {
  type: 'dot_tick' | 'entry_damage' | 'zone_expired' | 'entity_exit';
  zoneId: string;
  zoneType: string;
  entityId?: string;
  dotDefId?: string;
  dotIntensity?: number;
  dotDurationMs?: number;
  dotStackingRule?: string;
  dotMaxStacks?: number;
  damage?: number;
  sourceSkillId: string;
}

// ---------------------------------------------------------------------------
// Entity shape for update()
// ---------------------------------------------------------------------------

export interface ZoneEntity {
  id: string;
  x: number;
  y: number;
}

// ---------------------------------------------------------------------------
// Tile size constant (pixels per tile)
// ---------------------------------------------------------------------------

const TILE_SIZE = 32;

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class ZoneSystem {
  private readonly zones = new Map<string, ActiveZone>();
  private nextId = 0;
  private elapsedMs = 0;

  /** Create a new zone. Returns its instance ID. */
  create(config: ZoneConfig): string {
    const id = config.id || `zone_${this.nextId++}`;
    const zone: ActiveZone = {
      config: { ...config, id },
      remainingMs: config.durationMs,
      currentRadiusTiles: config.radiusTiles ?? 0,
      entitiesInside: new Set(),
      entityEntryTimes: new Map(),
      usedByEntities: new Set(),
    };
    this.zones.set(id, zone);
    return id;
  }

  /**
   * Move a zone's origin by `tiles` in the specified direction.
   * Used by Cultivate (Spread upgrade) directional shove: shifts the freshly-
   * planted zone 1 tile in the aimed direction before it locks in place.
   *
   * @param zoneId  Target zone ID.
   * @param dx      Normalized X component of the direction vector (-1, 0, or 1).
   * @param dy      Normalized Y component of the direction vector (-1, 0, or 1).
   * @param tiles   Distance in tiles to move (default 1).
   */
  moveZone(zoneId: string, dx: number, dy: number, tiles = 1): void {
    const zone = this.zones.get(zoneId);
    if (!zone) return;
    const TILE_SIZE = 32; // px per tile — matches world coordinate convention
    (zone.config as { position: { x: number; y: number } }).position = {
      x: zone.config.position.x + dx * tiles * TILE_SIZE,
      y: zone.config.position.y + dy * tiles * TILE_SIZE,
    };
  }

  /** Remove a zone early (no onExpiry callback). */
  remove(zoneId: string): void {
    this.zones.delete(zoneId);
  }

  /** Remove all zones. */
  removeAll(): void {
    this.zones.clear();
  }

  /** Get all active zones. */
  getAll(): ReadonlyArray<ActiveZone> {
    return Array.from(this.zones.values());
  }

  /** Get a zone by ID. */
  getZone(zoneId: string): ActiveZone | undefined {
    return this.zones.get(zoneId);
  }

  /** Query zones at a world position. */
  getZonesAt(x: number, y: number): ActiveZone[] {
    const result: ActiveZone[] = [];
    for (const zone of this.zones.values()) {
      if (this.isInsideZone(zone, x, y)) {
        result.push(zone);
      }
    }
    return result;
  }

  /** Check if a position is inside any zone of a given type. */
  isInsideType(x: number, y: number, zoneType: string): boolean {
    for (const zone of this.zones.values()) {
      if (zone.config.type === zoneType && this.isInsideZone(zone, x, y)) {
        return true;
      }
    }
    return false;
  }

  /** Combined speed multiplier at a position (product of all zone slows). */
  getSpeedMultiplierAt(x: number, y: number): number {
    let mult = 1.0;
    for (const zone of this.zones.values()) {
      if (zone.config.speedMultiplier !== undefined && this.isInsideZone(zone, x, y)) {
        mult *= zone.config.speedMultiplier;
      }
    }
    return mult;
  }

  /** Check if dash is suppressed at a position. */
  isDashSuppressedAt(x: number, y: number): boolean {
    for (const zone of this.zones.values()) {
      if (zone.config.suppressDash && this.isInsideZone(zone, x, y)) return true;
    }
    return false;
  }

  /** Check if charge is suppressed at a position. */
  isChargeSuppressedAt(x: number, y: number): boolean {
    for (const zone of this.zones.values()) {
      if (zone.config.suppressCharge && this.isInsideZone(zone, x, y)) return true;
    }
    return false;
  }

  /** Check if a position is blocked by an impassable zone. */
  isImpassableAt(x: number, y: number): boolean {
    for (const zone of this.zones.values()) {
      if (zone.config.impassable && this.isInsideZone(zone, x, y)) return true;
    }
    return false;
  }

  /** Check if a position is blocked for projectiles. */
  isBlockedByZoneAt(x: number, y: number): boolean {
    for (const zone of this.zones.values()) {
      if (zone.config.blocksProjectiles && this.isInsideZone(zone, x, y)) return true;
    }
    return false;
  }

  /**
   * Extend the remaining duration of all active zones matching a given type.
   * Used by Smothered (choke_passive) to retroactively add duration to
   * existing smoke_cloud zones when the passive unlocks mid-run.
   * Permanent zones (remainingMs === -1) are not affected.
   */
  extendZonesByType(zoneType: string, bonusMs: number): void {
    for (const zone of this.zones.values()) {
      if (zone.config.type === zoneType && zone.remainingMs !== -1) {
        zone.remainingMs += bonusMs;
      }
    }
  }

  /** Get how long an entity has been inside a zone (ms). Returns 0 if not inside. */
  getEntityDwellTimeMs(zoneId: string, entityId: string): number {
    const zone = this.zones.get(zoneId);
    if (!zone) return 0;
    const entry = zone.entityEntryTimes.get(entityId);
    if (entry === undefined) return 0;
    return this.elapsedMs - entry;
  }

  // -----------------------------------------------------------------------
  // Update
  // -----------------------------------------------------------------------

  /**
   * Tick all zones. Called each frame.
   *
   * - Moves drifting/following zones
   * - Expands growing zones
   * - Tracks entity entry/exit
   * - Fires DoT ticks and entry damage
   * - Expires zones
   *
   * Returns events for the caller to process (DoT application, entry damage, etc.).
   */
  update(delta: number, entities: Iterable<ZoneEntity>): ZoneEvent[] {
    this.elapsedMs += delta;
    const events: ZoneEvent[] = [];
    const expired: string[] = [];
    const entityArray = Array.from(entities);

    for (const [zoneId, zone] of this.zones) {
      const cfg = zone.config;

      // Move drifting zones
      if (cfg.velocity) {
        cfg.position.x += cfg.velocity.dx * delta;
        cfg.position.y += cfg.velocity.dy * delta;
      }

      // Follow entity
      if (cfg.followEntity) {
        const target = entityArray.find((e) => e.id === cfg.followEntity);
        if (target) {
          cfg.position.x = target.x;
          cfg.position.y = target.y;
        }
      }

      // Expand zone
      if (cfg.expandsOverTime && cfg.expansionRateMs && cfg.maxRadiusTiles) {
        const tilesPerMs = (cfg.expansionTiles ?? 1.0) / cfg.expansionRateMs;
        zone.currentRadiusTiles = Math.min(
          zone.currentRadiusTiles + tilesPerMs * delta,
          cfg.maxRadiusTiles,
        );
      }

      // Track entity presence
      const nowInside = new Set<string>();
      for (const entity of entityArray) {
        if (this.isInsideZone(zone, entity.x, entity.y)) {
          nowInside.add(entity.id);
        }
      }

      // Entity entries
      for (const entityId of nowInside) {
        if (!zone.entitiesInside.has(entityId)) {
          // New entry
          zone.entityEntryTimes.set(entityId, this.elapsedMs);
          cfg.onEntityEnter?.(entityId, zone);

          // One-shot check
          if (cfg.oneShot && zone.usedByEntities.has(entityId)) continue;

          // Entry damage
          if (cfg.entryDamage !== undefined && cfg.entryDamage > 0) {
            events.push({
              type: 'entry_damage',
              zoneId,
              zoneType: cfg.type,
              entityId,
              damage: cfg.entryDamage,
              sourceSkillId: cfg.sourceSkillId,
            });
            if (cfg.oneShot) zone.usedByEntities.add(entityId);
          }
        }
      }

      // Entity exits
      for (const entityId of zone.entitiesInside) {
        if (!nowInside.has(entityId)) {
          const entryTime = zone.entityEntryTimes.get(entityId);
          const dwellMs = entryTime !== undefined ? this.elapsedMs - entryTime : 0;
          zone.entityEntryTimes.delete(entityId);
          // Resolve exit position from current entity array (entity just moved out this frame)
          const exitEntity = entityArray.find((e) => e.id === entityId);
          const exitX = exitEntity?.x ?? cfg.position.x;
          const exitY = exitEntity?.y ?? cfg.position.y;
          cfg.onEntityExit?.(entityId, zone, dwellMs, exitX, exitY);
          events.push({
            type: 'entity_exit',
            zoneId,
            zoneType: cfg.type,
            entityId,
            sourceSkillId: cfg.sourceSkillId,
          });
        }
      }

      // Update entitiesInside
      zone.entitiesInside.clear();
      for (const id of nowInside) {
        zone.entitiesInside.add(id);
      }

      // DoT ticks — gated by tickIntervalMs
      if (cfg.dotDefId) {
        const tickInterval = cfg.tickIntervalMs ?? 500;
        for (const entityId of zone.entitiesInside) {
          if (cfg.oneShot && zone.usedByEntities.has(entityId)) continue;

          const entryTime = zone.entityEntryTimes.get(entityId) ?? this.elapsedMs;
          const dwellMs = this.elapsedMs - entryTime;
          const expectedTicks = Math.floor(dwellMs / tickInterval);
          // Simple check: emit a tick if enough time has passed since last tick
          // We emit once per update if the entity has been inside long enough
          if (expectedTicks > 0 && dwellMs % tickInterval < delta + 1) {
            events.push({
              type: 'dot_tick',
              zoneId,
              zoneType: cfg.type,
              entityId,
              dotDefId: cfg.dotDefId,
              dotIntensity: cfg.dotIntensity,
              dotDurationMs: cfg.dotDurationMs,
              dotStackingRule: cfg.dotStackingRule,
              dotMaxStacks: cfg.dotMaxStacks,
              sourceSkillId: cfg.sourceSkillId,
            });
            // For oneShot zones, mark entity used after the first DoT tick so
            // subsequent ticks (and re-entries) are blocked. This supports
            // toxic_footprint and contact_coating zones that apply one DoT
            // per entity via dotDefId rather than entryDamage.
            if (cfg.oneShot) zone.usedByEntities.add(entityId);
          }
        }
      }

      // Tick duration
      if (zone.remainingMs !== -1) {
        zone.remainingMs -= delta;
        if (zone.remainingMs <= 0) {
          cfg.onExpiry?.(zone);
          events.push({
            type: 'zone_expired',
            zoneId,
            zoneType: cfg.type,
            sourceSkillId: cfg.sourceSkillId,
          });
          expired.push(zoneId);
        }
      }
    }

    // Remove expired zones
    for (const id of expired) {
      this.zones.delete(id);
    }

    return events;
  }

  // -----------------------------------------------------------------------
  // Geometry: point-in-zone tests
  // -----------------------------------------------------------------------

  private isInsideZone(zone: ActiveZone, x: number, y: number): boolean {
    const cfg = zone.config;
    switch (cfg.shape) {
      case 'circle':
        return this.isInsideCircle(cfg.position, zone.currentRadiusTiles, x, y);
      case 'cone':
        return this.isInsideCone(cfg, x, y);
      case 'line':
        return this.isInsideLine(cfg, x, y);
      default:
        return false;
    }
  }

  private isInsideCircle(
    center: { x: number; y: number },
    radiusTiles: number,
    x: number,
    y: number,
  ): boolean {
    const radiusPx = radiusTiles * TILE_SIZE;
    const dx = x - center.x;
    const dy = y - center.y;
    return dx * dx + dy * dy <= radiusPx * radiusPx;
  }

  private isInsideCone(cfg: ZoneConfig, x: number, y: number): boolean {
    const lengthPx = (cfg.lengthTiles ?? 0) * TILE_SIZE;
    const halfAngle = ((cfg.angleRange ?? 60) / 2) * (Math.PI / 180);
    const facing = cfg.facingAngle ?? 0;

    const dx = x - cfg.position.x;
    const dy = y - cfg.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > lengthPx) return false;

    const angle = Math.atan2(dy, dx);
    let diff = angle - facing;
    // Normalize to [-PI, PI]
    while (diff > Math.PI) diff -= 2 * Math.PI;
    while (diff < -Math.PI) diff += 2 * Math.PI;

    return Math.abs(diff) <= halfAngle;
  }

  private isInsideLine(cfg: ZoneConfig, x: number, y: number): boolean {
    const lengthPx = (cfg.lengthTiles ?? 0) * TILE_SIZE;
    const widthPx = (cfg.widthTiles ?? 1) * TILE_SIZE;
    const facing = cfg.facingAngle ?? 0;
    const halfWidth = widthPx / 2;

    // Transform point into line-local coordinates
    const dx = x - cfg.position.x;
    const dy = y - cfg.position.y;
    const cos = Math.cos(-facing);
    const sin = Math.sin(-facing);
    const localX = dx * cos - dy * sin;
    const localY = dx * sin + dy * cos;

    return localX >= 0 && localX <= lengthPx && Math.abs(localY) <= halfWidth;
  }
}
